/**
 * MCP connector page translations (TKT-2026-0849).
 *
 * Surfaces three sections on /dashboard/mcp: Connection info, Tool
 * catalog, and Connector setup. Per-user API key issuance (Phase 2,
 * TKT-2026-0850) will reuse + extend these strings.
 */
export const mcp = {
  en: {
    pageTitle: "MCP",
    pageSubtitle: "Model Context Protocol — connect Claude or any MCP client to Solariis.",
    accessDenied: "This page is for admin and team members only.",
    sections: {
      connection: "Connection",
      connectionSubtitle:
        "URL and authentication scheme. The API key value is not shown here — Phase 1 uses a shared static secret managed by Marco.",
      toolCatalog: "Tool catalog",
      toolCatalogSubtitle:
        "Tools currently registered on the worker, grouped by domain. The catalog is regenerated from the MCP worker source on every dashboard build.",
      connectorSetup: "Connector setup",
      connectorSetupSubtitle:
        "Copy-paste snippets for popular MCP-compatible clients. Replace <YOUR_API_KEY> with the Bearer token shared by Marco.",
    },
    connection: {
      urlLabel: "MCP URL",
      authLabel: "Auth scheme",
      authValueHeader: "Authorization: Bearer <YOUR_API_KEY>",
      authValueQuery: "or ?key=<YOUR_API_KEY> query parameter",
      keyNote:
        "The API key itself is not shown on this page. Per-user keys with reveal-on-issue UX land in Phase 2.",
    },
    toolCatalog: {
      totalLabel: "tools",
      domainsLabel: "domains",
      generatedLabel: "Catalog generated at",
      emptyDomain: "No tools in this domain.",
      noDescription: "(no description)",
    },
    connector: {
      copy: "Copy",
      copied: "Copied!",
      copyAriaLabel: "Copy snippet to clipboard",
    },
    placeholder: {
      apiKey: "<YOUR_API_KEY>",
    },
    myKeys: {
      sectionTitle: "My keys",
      sectionSubtitle:
        "Per-user MCP API keys for your own connectors. Phase 2.A — issue + revoke. Reveal happens exactly once at issue time; rotate by revoke + issue-new.",
      empty: "You have no MCP keys issued. Create one below to connect a client.",
      tableName: "Name",
      tableHandle: "Handle",
      tableCreated: "Created",
      tableLastUsed: "Last used",
      tableStatus: "Status",
      tableActions: "Actions",
      statusActive: "Active",
      statusDisabled: "Revoked",
      revoke: "Revoke",
      revokeConfirm: "Revoke this key? Any client using it will be locked out.",
      neverUsed: "Never",
      issueFormTitle: "Issue a new key",
      issueFormHandleLabel: "Handle",
      issueFormHandleHelp: "Short slug (1-24 chars, lowercase, a-z 0-9 -). Used as part of the key string.",
      issueFormNameLabel: "Name",
      issueFormNameHelp: "Human label, e.g. \"My Claude Desktop\".",
      issueFormNoteLabel: "Note (optional)",
      issueFormSubmit: "Issue key",
      issueFormSubmitting: "Issuing…",
      revealTitle: "Your new key — save it now",
      revealNote:
        "This is the only time the plaintext key is shown. Copy it to your connector config before closing this dialog.",
      revealDismiss: "I've saved it",
      errorGeneric: "Something went wrong. Try again.",
    },
  },
  es: {
    pageTitle: "MCP",
    pageSubtitle: "Model Context Protocol — conecta Claude o cualquier cliente MCP a Solariis.",
    accessDenied: "Esta página es solo para admin y miembros del equipo.",
    sections: {
      connection: "Conexión",
      connectionSubtitle:
        "URL y esquema de autenticación. El valor de la API key no se muestra aquí — Fase 1 usa un secreto estático compartido gestionado por Marco.",
      toolCatalog: "Catálogo de herramientas",
      toolCatalogSubtitle:
        "Herramientas registradas en el worker, agrupadas por dominio. El catálogo se regenera desde el código del worker en cada build del dashboard.",
      connectorSetup: "Configuración del conector",
      connectorSetupSubtitle:
        "Snippets copy-paste para clientes MCP populares. Reemplaza <YOUR_API_KEY> con el token Bearer compartido por Marco.",
    },
    connection: {
      urlLabel: "URL del MCP",
      authLabel: "Esquema de auth",
      authValueHeader: "Authorization: Bearer <YOUR_API_KEY>",
      authValueQuery: "o query parameter ?key=<YOUR_API_KEY>",
      keyNote:
        "El valor de la API key no se muestra en esta página. Las claves por-usuario con reveal-on-issue llegan en Fase 2.",
    },
    toolCatalog: {
      totalLabel: "herramientas",
      domainsLabel: "dominios",
      generatedLabel: "Catálogo generado el",
      emptyDomain: "Sin herramientas en este dominio.",
      noDescription: "(sin descripción)",
    },
    connector: {
      copy: "Copiar",
      copied: "¡Copiado!",
      copyAriaLabel: "Copiar snippet al portapapeles",
    },
    placeholder: {
      apiKey: "<TU_API_KEY>",
    },
    myKeys: {
      sectionTitle: "Mis claves",
      sectionSubtitle:
        "Claves API MCP por-usuario para tus propios conectores. Fase 2.A — emitir + revocar. La clave se revela una sola vez al emitir; para rotar, revoca y emite una nueva.",
      empty: "No tienes claves MCP emitidas. Crea una abajo para conectar un cliente.",
      tableName: "Nombre",
      tableHandle: "Handle",
      tableCreated: "Creada",
      tableLastUsed: "Último uso",
      tableStatus: "Estado",
      tableActions: "Acciones",
      statusActive: "Activa",
      statusDisabled: "Revocada",
      revoke: "Revocar",
      revokeConfirm: "¿Revocar esta clave? Cualquier cliente que la use quedará bloqueado.",
      neverUsed: "Nunca",
      issueFormTitle: "Emitir una nueva clave",
      issueFormHandleLabel: "Handle",
      issueFormHandleHelp: "Slug corto (1-24 chars, minúsculas, a-z 0-9 -). Se usa como parte de la clave.",
      issueFormNameLabel: "Nombre",
      issueFormNameHelp: "Etiqueta legible, ej. \"Mi Claude Desktop\".",
      issueFormNoteLabel: "Nota (opcional)",
      issueFormSubmit: "Emitir clave",
      issueFormSubmitting: "Emitiendo…",
      revealTitle: "Tu nueva clave — guárdala ahora",
      revealNote:
        "Esta es la única vez que la clave en texto plano se muestra. Cópiala a la configuración de tu conector antes de cerrar este diálogo.",
      revealDismiss: "Listo, la guardé",
      errorGeneric: "Algo salió mal. Inténtalo otra vez.",
    },
  },
};
